import { HttpClient, HttpEventType } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { Platform } from "@ionic/angular";
import { tap } from "rxjs/operators";
import { environment } from "src/environments/environment";

import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { iMedia } from "src/model/interface/iMedia";
import { EventService } from "./event.service";
import { LoggerService } from "./logger.service";
import { ToastService } from "./toast.service";
import { TranslateService } from "@ngx-translate/core";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { ToastType } from "src/components/shared-components/enum/enumMisc";

@Injectable({
  providedIn: "root",
})
export class MediaService {
  private _logContext: string = "Media.service";

  /** Error code of the Filesystem plugin when the user denies the storage permission */
  private static readonly PERMISSION_DENIED_CODE = "OS-PLUG-FILE-0007";

  /** Error message of the Share plugin when the user closes the share sheet without picking anything */
  private static readonly SHARE_CANCELED_MESSAGE = "Share canceled";

  constructor(
    private _http: HttpClient,
    private eventService: EventService,
    private _platform: Platform,
    private _loggerService: LoggerService,
    private _toastService: ToastService,
    private _translationService: TranslateService,
  ) {}

    /**
   *  Convert the byte array to a json string
   * @param byteArray The byte array
   * @returns
   */
  public byteArrayToJsonString(byteArray: string): string {
    const parsedData = JSON.parse(byteArray);
    const jsonString = parsedData.data
      .map((byte: number) => String.fromCharCode(byte))
      .join("");
    return jsonString;
  }

  /**
   *  Convert the json string data to a blob
   * @param base64Data  The base64 data
   * @returns
   */
  public jsonStringToBlob(jsonString: string): Blob {
    const parsedData = JSON.parse(jsonString);
    const byteArray = new Uint8Array(parsedData.data);
    return new Blob([byteArray], { type: "application/octet-stream" });
  }

  /**
   * Upload single file to server; this file could be a typescript "File" object (as defined in ) OR a blob-like object (IE. a real "Blob"-type
   * object or "any"-type object, created using the constructor " new Blob(...)" and modified as needed.)
   *
   * NOTE:
   *
   * + If "fileToUpload" is a File object, then the name specified in "fileName" is already automatically taken from the name of the original, seleted file.
   * Therefore, the parameter "fileName" MUST NOT BE SPECIFIED.
   *
   * + If "fileToUpload" is a Blob-like object, then the parameter "fileName" MUST BE SPECIFIED.
   *
   * @param fileToUpload single file to upload to server
   * @param fileName Name of the file, as codified in the "ContentDisposition" header's parameter "filename" (OPTIONAL, depending on fileToUpload's type)
   */
  

  /**
   * Covert base64 file to blob
   *
   * @param base64String String base 64 to covert
   * @returns
   */
  public async convertBase64FileToBlob(base64String: any): Promise<Blob> {
    if (this._platform.is("android")) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        " convertBase64FileToBlob() ",
        "Android"
      );
      const response: Response = await fetch(base64String);

      if (!response.ok) {
        throw new Error(
          "Media.serv IN convertBase64FileToBlob HTTP error! status: " +
            response.status
        );
      } else {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          " convertBase64FileToBlob() ",
          "Media.serv IN convertBase64FileToBlob. Data fetched"
        );

        return await response.blob();
      }
    }

    /* iOS (and any other platform): "fetch()" is not reliable on "data:" URLs inside WKWebView, therefore
    the XHR-based conversion is used as the default. */
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      " convertBase64FileToBlob() ",
      "iOS / other platform"
    );

    return this.urlToBlob(base64String);
  }

  /**
   * Covert base64 file to blob.
   *
   * ATTENTION: this method MUST be called in case the OS running the app is "iOS".
   * @param url String base 64 to covert
   * @returns
   */
  public urlToBlob(url: any): Promise<Blob> {
    return new Promise((resolve, reject) => {
      var xhr = new XMLHttpRequest();
      xhr.onerror = () =>
        reject(new Error("urlToBlob(): network error while reading the URL"));
      xhr.onreadystatechange = () => {
        if (xhr.readyState !== 4) {
          return;
        }

        /* ATTENTION: a "data:" URL resolves with status 0, therefore only an explicit HTTP failure must be rejected.
        What really matters is that the response is an actual, non-empty Blob: without this check a failed request
        would silently resolve to "null" and the caller would end up writing an empty file on the device. */
        if (xhr.status !== 0 && (xhr.status < 200 || xhr.status >= 300)) {
          reject(
            new Error("urlToBlob(): HTTP error! status: " + xhr.status)
          );
          return;
        }

        if (!(xhr.response instanceof Blob) || xhr.response.size === 0) {
          reject(
            new Error(
              "urlToBlob(): the URL did not resolve to a valid, non-empty Blob"
            )
          );
          return;
        }

        resolve(xhr.response);
      };
      xhr.open("GET", url);
      xhr.responseType = "blob"; // convert type

      xhr.send();
    });
  }

  /**
   * Upload single file to server; this file could be a typescript "File" object (as defined in ) OR a blob-like object (IE. a real "Blob"-type
   * object or "any"-type object, created using the constructor " new Blob(...)" and modified as needed.)
   *
   * NOTE:
   *
   * + If "fileToUpload" is a File object, then the name specified in "fileName" is already automatically taken from the name of the original, seleted file.
   * Therefore, the parameter "fileName" MUST NOT BE SPECIFIED.
   *
   * + If "fileToUpload" is a Blob-like object, then the parameter "fileName" MUST BE SPECIFIED.
   *
   * @param fileToUpload single file to upload to server
   * @param fileName Name of the file, as codified in the "ContentDisposition" header's parameter "filename" (OPTIONAL, depending on fileToUpload's type)
   */
  public uploadFileToServer(
    fileToUpload: any,
    fileName: string = ""
  ): Promise<any> {
    let formData = new FormData();

    if (fileName) {
      formData.append("fileToUpload", fileToUpload, fileName);
    } else {
      formData.append("fileToUpload", fileToUpload);
    }

    /** Set it to observe body upload (ex. for calculating percentace of upload) */
    const config = {
      reportProgress: true,
      observe: "events" as "body",
    };

    return this._http
      .post(
        environment.httpServerRoot + AppSettings.API_URL_FILE_UPLOAD,
        formData,
        config
      )
      .pipe(
        tap((event: any) => {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_http.post - " + AppSettings.API_URL_FILE_UPLOAD,
            "uploadFileToServer()",
            "Event: " + JSON.stringify(event)
          );
          if (event.type === HttpEventType.UploadProgress) {
            const percenteUploaded: number = Math.round(
              (100 * event.loaded) / event.total
            );
            this.eventService.OnMediaUploadInProgressEvent.next(
              percenteUploaded
            );
          } else if (event.type === HttpEventType.Response) {
            const mediaUploaded: iMedia = event.body;

            this.eventService.OnMediaUploadComplete.next(mediaUploaded);
          } else if (event.type === HttpEventType.Sent) {
            this.eventService.OnMediaUploadInProgressEvent.next(0);
          } else {
            /** If object emit with status different to 200, something went wrong */
            if (event.status && event.status !== 200) {
              this.eventService.OnMediaUploadAbort.next(null);
            }
          }
        })
      )
      .toPromise();
  }

  /**
   * Save a file where the user can find it and tell them how it went: on Android in the public Download folder, with a
   * toast naming it; on iOS in the private cache folder of the app, then the native share sheet, from which the user
   * picks the final destination (IE. "Save to Files"), and the cached copy is deleted afterwards.
   *
   * ATTENTION: on iOS NO success toast is shown on purpose. The native window IS the feedback, and it reports the same
   * result whether the user completed the save or simply dismissed it, so a toast could claim a success that never
   * happened.
   *
   * @param blob The content of the file.
   * @param namefile The name given to the file, extension included.
   */
  public async handleMobileDownload(blob: Blob, namefile: string): Promise<void> {
    const isIos: boolean = this._platform.is("ios");
    let fileUri: string;

    try {
      fileUri = isIos ? await this.writeTempFile(namefile, blob) : await this.saveFileForUser(namefile, blob);
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "handleMobileDownload()",
        "ERROR while saving '" + namefile + "': " + (exception?.message ?? JSON.stringify(exception))
      );

      await this._toastService.showToast(
        ToastType.ERROR,
        this._translationService.instant(
          this.isPermissionDenied(exception) ? "dictionary.permissionDenied" : "dictionary.opsSomethingWentWrong"
        ),
        3000
      );
      return;
    }

    if (isIos) {
      await this.shareTempFile(fileUri, namefile);
      return;
    }

    await this._toastService.showToast(
      ToastType.SUCCESS,
      this._translationService.instant("dictionary.fileSavedCorretly") + "/Download/" + fileUri.split("/").pop(),
      3000
    );
  }

  /**
   * Hand a file published by the server (IE. a generated report) to the user: on iOS the file is downloaded natively and
   * then offered in the share sheet, everywhere else it is opened in a new window as before.
   *
   * ATTENTION: on iOS "window.open()" cannot be used here. The URL is known only after an async call to the server, and
   * WKWebView silently blocks a window not opened directly by a tap, so the user would see nothing at all.
   *
   * @param fileUrl full URL of the file on the server
   */
  public async openServerFile(fileUrl: string): Promise<void> {
    if (!this._platform.is("ios")) {
      window.open(fileUrl);
      return;
    }

    const fileName: string = decodeURIComponent(fileUrl.split("?")[0].split("/").pop());
    let fileUri: string;

    try {
      await Filesystem.downloadFile({ url: fileUrl, path: fileName, directory: Directory.Cache, recursive: true });

      fileUri = (await Filesystem.getUri({ path: fileName, directory: Directory.Cache })).uri;
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "openServerFile()",
        "ERROR while downloading '" + fileUrl + "': " + (exception?.message ?? JSON.stringify(exception))
      );

      await this.showErrorToastForFileDownload();
      return;
    }

    await this.shareTempFile(fileUri, fileName);
  }

  /**
   * iOS only: write a file in the private cache folder of the app, to be handed to {@link shareTempFile}. Unlike the
   * Documents folder, the cache is not shown by the Files app, so the user does not find a second copy there.
   *
   * @param fileName file name, with its extension
   * @param blob content of the file
   * @returns the URI of the written file
   */
  private async writeTempFile(fileName: string, blob: Blob): Promise<string> {
    const result = await Filesystem.writeFile({
      path: fileName,
      data: await this.blobToBase64(blob),
      directory: Directory.Cache,
      recursive: true,
    });

    return result.uri;
  }

  /**
   * iOS only: offer a file of the cache folder in the share sheet, then delete it, whether the user saved it or not.
   *
   * @param fileUri URI of the file, as returned by {@link writeTempFile}
   * @param fileName name of the file inside the cache folder
   */
  private async shareTempFile(fileUri: string, fileName: string): Promise<void> {
    try {
      await this.shareFile(fileUri);
    } catch (exception) {
      if (!this.isShareCanceled(exception)) {
        this._loggerService.log(
          LogLevel.LOG_ERROR,
          this._logContext,
          this._logContext,
          "shareTempFile()",
          "The native share sheet could not be presented: " + (exception?.message ?? JSON.stringify(exception))
        );

        await this.showErrorToastForFileDownload();
      }
    } finally {
      await Filesystem.deleteFile({ path: fileName, directory: Directory.Cache }).catch(() => undefined);
    }
  }

  /**
   * Save a file where the user can find it: on Android in the public Download folder, on iOS in the Documents folder
   * of the app, which the Files app shows. On Android 10 and older the Filesystem plugin asks for the storage
   * permission and rejects when the user denies it.
   *
   * ATTENTION: Android does not let the app overwrite a file in Download that it did not write itself (IE. one left by
   * the Cordova app, or by a previous install), so a name already taken there gets a timestamp suffix instead.
   *
   * @param fileName file name, with its extension
   * @param blob content of the file
   * @returns the URI of the saved file
   */
  public async saveFileForUser(fileName: string, blob: Blob): Promise<string> {
    const isAndroid: boolean = this._platform.is("android");
    const directory: Directory = isAndroid ? Directory.ExternalStorage : Directory.Documents;
    const folder: string = isAndroid ? "Download/" : "";
    const finalFileName: string = isAndroid ? await this.resolveAvailableDownloadName(folder, fileName) : fileName;

    const result = await Filesystem.writeFile({
      path: folder + finalFileName,
      data: await this.blobToBase64(blob),
      directory: directory,
      recursive: true,
    });

    this._loggerService.log(LogLevel.LOG_INFO, this._logContext, this._logContext, "saveFileForUser()", "Saved: " + result.uri);

    return result.uri;
  }

  /**
   * Tell whether {@link saveFileForUser} failed because the user denied the storage permission.
   *
   * @param error error thrown by saveFileForUser
   * @returns true when the user denied the permission
   */
  public isPermissionDenied(error: unknown): boolean {
    return (error as { code?: string } | null)?.code === MediaService.PERMISSION_DENIED_CODE;
  }

  /**
   * Present the native share sheet for a file. On iOS it is the window offering "Save to Files", unlike the "open with"
   * menu, which lists just the apps able to open the file and none at all for some file types.
   *
   * @param fileUri URI of the file, as returned by {@link writeTempFile}
   */
  public async shareFile(fileUri: string): Promise<void> {
    await Share.share({ files: [fileUri] });
  }

  /**
   * Tell whether {@link shareFile} failed only because the user closed the share sheet without picking anything.
   *
   * @param error error thrown by shareFile
   * @returns true when the user dismissed the share sheet
   */
  private isShareCanceled(error: unknown): boolean {
    return (error as { message?: string } | null)?.message === MediaService.SHARE_CANCELED_MESSAGE;
  }

  /**
   * Content of a blob in base64, the format the Filesystem plugin writes.
   *
   * @param blob content to convert
   * @returns the base64 string, without the "data:...;base64," prefix
   */
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onload = () => {
        const dataUrl = reader.result as string;
        resolve(dataUrl.substring(dataUrl.indexOf(",") + 1));
      };
      reader.readAsDataURL(blob);
    });
  }

  /**Utility method, display generic red error toast if an unexpeted error happened in any point of the download of the file */
  private async showErrorToastForFileDownload(): Promise<void> {
    await this._toastService.showToast(
      ToastType.ERROR,
      this._translationService.instant("dictionary.opsSomethingWentWrong"),
      3000
    );
  }

  /**
   * Download the file on the browser or desktop
   * @param blob  The file blob
   * @param fileName  The file name
   */
  public handleDesktopBrowserDownload(fileName: string, blob: Blob) {
    const anchor = document.createElement("a");
    anchor.download = fileName;
    anchor.href = (window.webkitURL || window.URL).createObjectURL(blob);
    anchor.click();
  }

  /**
   * This method dinamically generates a suffix to be added to the end of a files's name in order for it to be diefferent form th one cretaed
   * previously or after.
   * The suffix respects the following pattern: CURRENT_YEAR + CURRENT_MONTH + CURRENT_DAY + CURRENT_LOCAL_HOUR + CURRENT_MINUTE + CURRENT_SECOND.
   *
   * EX: If file is created on 24 January 2025, at 11:49:38, the name shall be "_20250124114938"
   * @returns The suffix
   */
  public generateFileIndividualSuffix() {
    let individualSuffix: string = "_";
    let today = new Date();
    let yyyy = today.getFullYear();
    let mm = String(today.getMonth() + 1).padStart(2, "0");
    let dd = String(today.getDate()).padStart(2, "0");
    let h = String(today.getHours()).padStart(2, "0");
    let m = String(today.getMinutes()).padStart(2, "0");
    let s = String(today.getSeconds()).padStart(2, "0");

    individualSuffix = individualSuffix + yyyy + mm + dd + h + m + s;

    return individualSuffix;
  }

  /**
   * Name to give to a file being moved into the public "Download" directory: the original one when it
   * is still free, otherwise the same name carrying a timestamp suffix (ex. "certificato.pdf" becomes
   * "certificato_20260729103045.pdf"), the way a browser turns a duplicate into "file(1).pdf".
   *
   * ATTENTION: this is not cosmetic. The system does not let the app overwrite a file already present
   * in "Download", so re-downloading the same document would make the whole operation fail with a
   * generic error instead of saving anything.
   *
   * If the check itself cannot be carried out, the original name is returned and the move is attempted
   * exactly as before: this method can only improve the outcome, never worsen it.
   *
   * @param downloadDirectoryPath Path of the "Download" directory in the external storage, terminating with a slash.
   * @param namefile Name the file would keep if it were free.
   * @returns A name that is expected to be free inside the "Download" directory.
   */
  private async resolveAvailableDownloadName(
    downloadDirectoryPath: string,
    namefile: string
  ): Promise<string> {
    let alreadyExists: boolean = false;

    try {
      await Filesystem.stat({ path: downloadDirectoryPath + namefile, directory: Directory.ExternalStorage });
      alreadyExists = true;
    } catch (exception) {
      /* "stat()" rejects both when the file is not there and when it cannot be inspected at all:
      in either case the original name is the right one to attempt. */
      return namefile;
    }

    if (!alreadyExists) {
      return namefile;
    }

    const suffixedName: string = this.addSuffixToFileName(
      namefile,
      this.generateFileIndividualSuffix()
    );

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      " resolveAvailableDownloadName() ",
      "'" +
        namefile +
        "' is already present in the 'Download' folder, saving it as '" +
        suffixedName +
        "'"
    );

    return suffixedName;
  }

  /**
   * Insert a suffix BEFORE the file extension, so the extension keeps identifying the file type
   * ("certificato.pdf" + "_2026..." gives "certificato_2026....pdf", not "certificato.pdf_2026...").
   * Names carrying no extension simply get the suffix appended.
   *
   * @param namefile Original file name.
   * @param suffix Suffix to insert.
   * @returns The name including the suffix.
   */
  private addSuffixToFileName(namefile: string, suffix: string): string {
    const lastDotIndex: number = namefile.lastIndexOf(".");

    /* "<= 0" also covers hidden names entirely made of an extension (ex. ".pdf"), whose dot must not
    be treated as an extension separator. */
    if (lastDotIndex <= 0) {
      return namefile + suffix;
    }

    return (
      namefile.slice(0, lastDotIndex) + suffix + namefile.slice(lastDotIndex)
    );
  }
}
